import type { Timestamp } from 'firebase/firestore'

// ✅ SIMPLIFIED NOTIFICATION TYPES: Match the new backend system
export interface SimplifiedNotification {
  readonly id: string
  readonly userId: string
  readonly type: 'engagement' | 'request' | 'support' | 'system'
  readonly subtype: string
  readonly message: string
  readonly data: Readonly<Record<string, any>>
  readonly audience: 'web' | 'mobile' | 'all'
  readonly read: boolean
  readonly createdAt: Timestamp
}

// ✅ NOTIFICATION EVENT TYPES: For creating events
export interface NotificationEvent {
  readonly id: string
  readonly type: 'interaction.created' | 'request.status_changed' | 'support.status_changed' | 'system.announcement'
  readonly payload: Readonly<Record<string, any>>
  readonly processed: boolean
  readonly createdAt: Timestamp
}

// ✅ NOTIFICATION COUNT TYPE: For dashboard counters
export interface NotificationCount {
  readonly total: number
  readonly unread: number
  readonly engagement?: number
  readonly requests?: number
  readonly support?: number
  readonly system?: number
}

// ✅ MUTATION INPUT TYPES: For React Query mutations
export interface CreateNotificationEventInput {
  readonly type: string
  readonly payload: Record<string, any>
}

export interface MarkAsReadInput {
  readonly notificationId: string
}

export interface MarkAllAsReadInput {
  readonly userId: string
}

export interface NotificationCountInput {
  readonly userId: string
  readonly companyId?: string
}

// ✅ QUERY FILTER TYPES: For filtering notifications
export interface NotificationFilters {
  readonly userId: string
  readonly audience?: readonly ('web' | 'mobile' | 'all')[]
  readonly type?: 'engagement' | 'request' | 'support' | 'system'
  readonly companyId?: string
  readonly read?: boolean
  readonly limit?: number
  readonly offset?: number
}

// ✅ GROUPED NOTIFICATION INTERFACE: For UI grouping
export interface GroupedNotification {
  readonly id: string
  readonly type: 'single' | 'grouped'
  readonly latestNotification: SimplifiedNotification
  readonly count: number
  readonly title?: string
  readonly message?: string
  readonly notifications?: readonly SimplifiedNotification[]
  readonly category: string
  readonly timestamp: Timestamp
  readonly read: boolean
}

// ✅ CATEGORY GROUP INTERFACE: For category-based organization
export interface CategoryGroup {
  readonly category: string
  readonly label: string
  readonly icon: string
  readonly color: string
  readonly description: string
  readonly notifications: readonly GroupedNotification[]
  readonly unreadCount: number
  readonly totalCount: number
}

// ✅ REALTIME SUBSCRIPTION OPTIONS: For Next.js 15+ realtime features
export interface RealtimeOptions {
  readonly enabled: boolean
  readonly audiences: readonly ('web' | 'mobile' | 'all')[]
  readonly companyId?: string
  readonly onError?: (error: Error) => void
  readonly onUpdate?: (notifications: readonly SimplifiedNotification[]) => void
}

// ✅ NOTIFICATION DISPLAY OPTIONS: For UI rendering
export interface NotificationDisplayOptions {
  readonly showGrouped: boolean
  readonly categoriesVisible: readonly string[]
  readonly sortBy: 'createdAt' | 'type' | 'category'
  readonly sortOrder: 'asc' | 'desc'
  readonly pageSize: number
  readonly showRead: boolean
}

// ✅ ERROR HANDLING TYPES: For defensive error handling
export interface NotificationError {
  readonly code: string
  readonly message: string
  readonly details?: Record<string, any>
  readonly timestamp: Date
}

export interface NotificationState {
  readonly notifications: readonly SimplifiedNotification[]
  readonly loading: boolean
  readonly error: NotificationError | null
  readonly hasMore: boolean
  readonly lastFetched: Date | null
}

// ✅ UI NOTIFICATION TYPES: For component props
export interface UINotification {
  readonly id: string
  readonly title: string
  readonly message: string
  readonly type: 'engagement' | 'request' | 'support' | 'system'
  readonly subtype: string
  readonly timestamp: Date
  readonly read: boolean
  readonly data?: Record<string, any>
}

// ✅ NOTIFICATION CONFIGURATION: UI rendering config
export const NotificationConfig = {
  engagement: {
    like: { icon: '❤️', color: 'text-red-600' },
    comment: { icon: '💬', color: 'text-blue-600' },
    share: { icon: '🔄', color: 'text-green-600' },
    reply: { icon: '↩️', color: 'text-purple-600' },
  },
  request: {
    follow_accepted: { icon: '✅', color: 'text-green-600' },
    follow_rejected: { icon: '❌', color: 'text-red-600' },
    join_approved: { icon: '🎉', color: 'text-yellow-600' },
    join_rejected: { icon: '❌', color: 'text-red-600' },
  },
  support: {
    status_changed: { icon: '🎫', color: 'text-blue-600' },
  },
  system: {
    announcement: { icon: '📢', color: 'text-orange-600' },
    maintenance: { icon: '🔧', color: 'text-gray-600' },
  },
} as const

// ✅ HELPER FUNCTIONS: Type guards and utilities
export const isValidNotificationType = (type: string): type is 'engagement' | 'request' | 'support' | 'system' => {
  return ['engagement', 'request', 'support', 'system'].includes(type)
}

export const isValidAudience = (audience: string): audience is 'web' | 'mobile' | 'all' => {
  return ['web', 'mobile', 'all'].includes(audience)
}

export const safeParseTimestamp = (timestamp: any): Date => {
  // If timestamp is null/undefined, return current time as fallback
  if (!timestamp || timestamp === null) {
    console.warn('Null/undefined timestamp detected, using current time as fallback')
    return new Date() // Current time instead of Unix epoch
  }

  try {
    // Handle Firestore Timestamp objects
    if (timestamp.toDate && typeof timestamp.toDate === 'function') {
      return timestamp.toDate()
    }

    // Handle timestamp objects with seconds property (Firestore format)
    if (timestamp.seconds !== undefined && typeof timestamp.seconds === 'number') {
      return new Date(timestamp.seconds * 1000 + (timestamp.nanoseconds || 0) / 1000000)
    }

    // Handle millisecond timestamps
    if (typeof timestamp === 'number') {
      return new Date(timestamp)
    }

    // Handle string timestamps
    if (typeof timestamp === 'string') {
      const parsed = new Date(timestamp)
      if (!Number.isNaN(parsed.getTime())) {
        return parsed
      }
    }

    // Handle Date objects
    if (timestamp instanceof Date) {
      return timestamp
    }

    // If we get here, we couldn't parse the timestamp
    console.warn('Could not parse timestamp, using fallback:', timestamp)
    return new Date(0) // Unix epoch instead of current time
  } catch (error) {
    console.error('Error parsing timestamp:', timestamp, error)
    return new Date(0) // Unix epoch instead of current time
  }
}
